// src/lib/evenements-dessin.js — LE DESSIN D'UN ÉVÉNEMENT, DÉCIDÉ UNE FOIS.
//
// ══ CE QUE DIMITRI A DEMANDÉ, MOT POUR MOT (2026-09-13) ════════════════════
//
// « Pas une carte. Un cercle fin de la couleur de la famille avec une icône
// dedans, une étiquette en petites capitales, une phrase en noir, et un
// lien-bouton. »
//
// Et, sur les icônes : « Une icône par NATURE, pas par famille : enveloppe pour
// un message, calendrier pour une réservation, horloge pour un ménage, euro
// pour l'argent, clé pour une caution, étoile pour un avis, crayon pour un
// contrat. Je dois savoir de quoi il parle sans lire. »
//
// ⚠️ D'OÙ DEUX TABLES SÉPARÉES, et c'est tout l'intérêt : la COULEUR dit ce que
// ça vaut (neuf / urgent / réglé), l'ICÔNE dit de quoi ça parle. Les mélanger —
// une icône d'alerte pour tout ce qui est urgent — reviendrait à dire deux fois
// la même chose et à ne jamais dire la seconde.

// La couleur de la famille. Trois, pas plus : au-delà, on ne les distingue
// plus d'un coup d'œil, et c'est justement ce qu'on demande à ce dessin.
// ══ LA PALETTE DU BLOC OPÉRATEUR — celle de la maquette, pas la charte publique ══
//
// Dimitri, 2026-09-13 : « tu n'as pas repris le design de ma maquette ». Les
// valeurs ci-dessous sont les variables `:root` de docs/handoff/admin-proto.html
// (lignes 6-10), lues une à une. La prod avait pris la charte publique FBZ
// (#87b8e6, #4a9d6e, #8f2f1d…), décalée d'un cran : le ciel public est le halo
// de la maquette, pas son ciel ; le terra public est son terra-tx, pas son
// terra. ⚠️ NE PAS « corriger » vers la charte publique : l'admin a sa palette.
export const PALETTE_OP = Object.freeze({
  navy: "#3d5166",
  navyD: "#233240",
  ciel: "#5c8ab8",
  cielP: "#e8f2fa",
  vert: "#7fc4a3",
  vertBg: "#f0f7f3",
  vertTx: "#124a33",
  terra: "#b8412b",
  terraBg: "#fdf4f0",
  terraTx: "#8f2f1d",
  terraBd: "#e0b5a5",
  gris: "#5c6773",
  grisD: "#98a2ad",
  bord: "#e3e8ee",
  fond: "#f8fafc",
});

export const FAMILLES = Object.freeze({
  // `.opEvL.neuf .ic{color:var(--ciel)}` / `.opEvL.neuf .go{color:var(--navy)}`
  nouveau: { teinte: PALETTE_OP.ciel, encre: PALETTE_OP.navy, mot: "nouveau" },
  // `.opEvL.urgent .ic{color:var(--terra)}` / `.go{color:var(--terra-tx)}`
  urgent: {
    teinte: PALETTE_OP.terra,
    encre: PALETTE_OP.terraTx,
    mot: "urgent",
  },
  // `.opEvL.regle .ic{color:var(--vert)}` / `.go{color:var(--vert-tx)}`
  regle: { teinte: PALETTE_OP.vert, encre: PALETTE_OP.vertTx, mot: "réglé" },
});

// L'icône de la NATURE. Des glyphes, pas une bibliothèque : le fil en affiche
// une douzaine, et charger un jeu d'icônes pour douze traits coûterait plus que
// tout le reste de l'écran.
export const ICONES = Object.freeze({
  message: "✉",
  reservation: "▣",
  menage: "◷",
  argent: "€",
  caution: "⚿",
  avis: "★",
  contrat: "✎",
  formulaire: "▤",
  incident: "◌",
  prix: "◈",
  // ── LES HUIT AUTRES, AJOUTÉES LE 2026-09-13 ────────────────────────────
  // Le moteur porte DIX-NEUF natures ; cette table n'en connaissait que onze.
  // Les huit manquantes tombaient sur le point neutre — l'icône cessait de dire
  // « de quoi ça parle », ce qui est sa seule raison d'être. Un test compare
  // désormais les deux listes et rougit à la prochaine nature ajoutée.
  //
  // ⚠️ `courriel` PARTAGE L'ENVELOPPE DE `message`, à dessein : c'est la même
  // chose vue d'un autre canal. Deux glyphes pour un même objet apprendraient
  // une distinction qui n'existe pas.
  courriel: "✉",
  signalement: "◌",
  tache: "☑",
  service: "✦",
  stock: "▢",
  controle: "◉",
  taxe: "§",
  sync: "⟳",
  promo: "%",
  // Deux réservations sur les mêmes dates : deux traits qui se croisent.
  conflit: "⧗",
  // Ce qu'il remarque tout seul n'a pas de nature d'objet : c'est un regard.
  remarque: "◈",
});

/**
 * Le dessin d'un événement — famille, icône, étiquette.
 *
 * ⚠️ UNE NATURE INCONNUE NE CASSE RIEN ET NE MENT PAS : elle rend un point
 * neutre et garde son mot. Un événement qu'on n'a pas prévu doit s'afficher
 * quand même — le manquer coûte plus cher que l'afficher sans son icône.
 */
export function dessinDe(ev) {
  const f = FAMILLES[(ev && ev.famille) || ""] || FAMILLES.nouveau;
  const icone = ICONES[(ev && ev.nature) || ""] || "●";
  return {
    teinte: f.teinte,
    encre: f.encre,
    icone,
    // L'étiquette dit la NATURE (« message », « réservation »), pas la famille :
    // la famille est déjà dite par la couleur. « Il remarque » est le seul cas
    // où l'étiquette dit autre chose — parce que là, ce n'est pas un fait qui
    // tombe, c'est lui qui parle.
    etiquette: ev && ev.remarque ? "il remarque" : (ev && ev.nature) || "",
  };
}

// ══ LE GRAS SUR LA TÊTE DE PHRASE ═══════════════════════════════════════════
//
// Dans la maquette, chaque phrase du catalogue commence par `<b>…</b>` sur le
// nom ou le logement. Les phrases de la prod viennent du SERVEUR en texte pur
// (lib/operateur-evenements.js), et l'écran n'injecte JAMAIS de HTML. On
// retrouve donc la tête par sa PONCTUATION : ce qui précède le premier « — »
// ou le premier « : » est ce dont il parle (« Chloé Raynal — Cocon Naturel. »,
// « Caution déposée par Marta Roig — Cocon Naturel… »).
//
// ⚠️ LA RÈGLE SE RETIENT QUAND ELLE NE SAIT PAS. Une tête trop longue
// (« Marta Roig (Booking) arrive le 12 sept à Cocon Naturel sans caution
// déposée : … ») n'est pas un sujet, c'est déjà la phrase : on ne met rien en
// gras plutôt que d'en mettre la moitié. Le plafond est celui d'un nom suivi
// d'un lieu, pas d'une proposition.
const TETE_MAX = 48;
const SEPARATEURS_TETE = [" — ", " : "];

/** `{ tete, reste }` — `tete` vaut `null` quand rien ne mérite le gras. */
export function scinderTete(phrase) {
  const p = typeof phrase === "string" ? phrase : "";
  let coupe = -1;
  let longueurSep = 0;
  for (const sep of SEPARATEURS_TETE) {
    const i = p.indexOf(sep);
    if (i > 0 && (coupe < 0 || i < coupe)) {
      coupe = i;
      longueurSep = sep.length;
    }
  }
  if (coupe < 0) return { tete: null, reste: p };
  const tete = p.slice(0, coupe);
  const reste = p.slice(coupe);
  const suite = p.slice(coupe + longueurSep);
  if (!tete.trim() || tete.length > TETE_MAX || !suite.trim()) {
    return { tete: null, reste: p };
  }
  return { tete, reste };
}
